import { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Timer } from 'lucide-react';
import type { Question } from '@solace-quiz/shared';

type QuestionViewProps = {
  question: Question;
  questionNumber: number;
  totalQuestions: number;
  onAnswer: (selectedIndex: number, isCorrect: boolean, timeTaken: number) => void;
  onSubmit: (selectedIndex: number, isCorrect: boolean, timeTaken: number) => Promise<void>;
  previousAnswer?: { selectedIndex: number; isCorrect: boolean; timeTaken: number };
  messages: {
    questionProgress: string;
    submitButton: string;
    nextButton: string;
    correctFeedback: string;
    incorrectFeedback: string;
  };
};

export default function QuestionView({
  question,
  questionNumber,
  totalQuestions,
  onAnswer,
  onSubmit,
  previousAnswer,
  messages,
}: QuestionViewProps) {
  const [shuffledOptions, setShuffledOptions] = useState<{ text: string; originalIndex: number }[]>([]);
  const [correctShuffledIndex, setCorrectShuffledIndex] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showResult, setShowResult] = useState(!!previousAnswer);
  const [startTime, setStartTime] = useState<number>(Date.now());
  const [elapsedTime, setElapsedTime] = useState<number>(previousAnswer?.timeTaken ?? 0);
  const [finalTime, setFinalTime] = useState<number>(previousAnswer?.timeTaken ?? 0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const optionsWithIndex = question.options.map((text, index) => ({ text, originalIndex: index }));
    const shuffled = [...optionsWithIndex].sort(() => Math.random() - 0.5);
    setShuffledOptions(shuffled);

    const correctIndex = shuffled.findIndex(opt => opt.originalIndex === question.correctAnswer);
    setCorrectShuffledIndex(correctIndex);

    if (previousAnswer) {
      const prevShuffledIndex = shuffled.findIndex(opt => opt.originalIndex === previousAnswer.selectedIndex);
      setSelectedAnswer(prevShuffledIndex);
      setShowResult(true);
      setElapsedTime(previousAnswer.timeTaken);
      setFinalTime(previousAnswer.timeTaken);
    } else {
      setSelectedAnswer(null);
      setShowResult(false);
      setStartTime(Date.now());
      setElapsedTime(0);
      setFinalTime(0);
    }

    setIsSubmitting(false);
  }, [question.id]);

  useEffect(() => {
    if (showResult) return;

    const interval = setInterval(() => {
      setElapsedTime(Date.now() - startTime);
    }, 10);

    return () => clearInterval(interval);
  }, [startTime, showResult]);

  const handleAnswerSelect = (index: number) => {
    if (showResult) return;
    setSelectedAnswer(index);
  };

  const handleSubmit = async () => {
    if (selectedAnswer === null || showResult || isSubmitting) return;

    setIsSubmitting(true);

    try {
      const originalIndex = shuffledOptions[selectedAnswer].originalIndex;
      const isCorrect = selectedAnswer === correctShuffledIndex;
      const timeTaken = Date.now() - startTime;
      setElapsedTime(timeTaken);
      setFinalTime(timeTaken);
      setShowResult(true);

      await onSubmit(originalIndex, isCorrect, timeTaken);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    if (selectedAnswer === null) return;
    const originalIndex = shuffledOptions[selectedAnswer].originalIndex;
    const isCorrect = selectedAnswer === correctShuffledIndex;
    onAnswer(originalIndex, isCorrect, finalTime);
  };

  const formatTime = (ms: number): string => {
    const totalSeconds = Math.floor(ms / 1000);
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    const milliseconds = Math.floor((ms % 1000) / 10);
    return `${mins}:${secs.toString().padStart(2, '0')}.${milliseconds.toString().padStart(2, '0')}`;
  };

  if (shuffledOptions.length === 0) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-light/30 to-brand-light/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-2xl">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium text-brand-green">
              {messages.questionProgress
                .replace('{current}', questionNumber.toString())
                .replace('{total}', totalQuestions.toString())}
            </span>
            <div className="flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-lg">
              <Timer className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-mono font-semibold text-gray-700">
                {formatTime(elapsedTime)}
              </span>
            </div>
          </div>
          <div className="flex gap-1 mb-4">
            {Array.from({ length: totalQuestions }).map((_, idx) => (
              <div
                key={idx}
                className={`h-2 w-8 rounded-full ${
                  idx < questionNumber - 1
                    ? 'bg-brand-green'
                    : idx === questionNumber - 1
                    ? 'bg-brand-green/50'
                    : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
          <h2 className="text-2xl font-bold text-brand-dark font-heading">{question.question}</h2>
        </div>

        <div className="space-y-3 mb-6">
          {shuffledOptions.map((option, index) => {
            const isSelected = selectedAnswer === index;
            const isCorrect = index === correctShuffledIndex;
            const showCorrectAnswer = showResult && isCorrect;
            const showIncorrectAnswer = showResult && isSelected && !isCorrect;

            return (
              <button
                key={index}
                onClick={() => handleAnswerSelect(index)}
                disabled={showResult}
                className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                  showCorrectAnswer
                    ? 'border-brand-green bg-brand-light/30'
                    : showIncorrectAnswer
                    ? 'border-red-500 bg-red-50'
                    : isSelected
                    ? 'border-brand-green bg-brand-light/30'
                    : 'border-gray-200 hover:border-brand-green/50 hover:bg-gray-50'
                } ${showResult ? 'cursor-default' : 'cursor-pointer'}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-brand-dark">{option.text}</span>
                  {showCorrectAnswer && (
                    <CheckCircle className="w-6 h-6 text-brand-green" />
                  )}
                  {showIncorrectAnswer && (
                    <XCircle className="w-6 h-6 text-red-600" />
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {showResult && (
          <div
            className={`mb-6 p-4 rounded-lg ${
              selectedAnswer === correctShuffledIndex
                ? 'bg-brand-light/30 border border-brand-green'
                : 'bg-red-50 border border-red-200'
            }`}
          >
            <p
              className={`font-semibold ${
                selectedAnswer === correctShuffledIndex ? 'text-brand-dark' : 'text-red-800'
              }`}
            >
              {selectedAnswer === correctShuffledIndex ? messages.correctFeedback : messages.incorrectFeedback}
            </p>
          </div>
        )}

        {!showResult ? (
          <button
            onClick={handleSubmit}
            disabled={selectedAnswer === null || isSubmitting}
            className="w-full bg-brand-green text-brand-dark py-3 rounded-lg font-semibold hover:opacity-90 transition transform active:scale-95 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Submitting...' : messages.submitButton}
          </button>
        ) : (
          <button
            onClick={handleNext}
            className="w-full bg-brand-green text-brand-dark py-3 rounded-lg font-semibold hover:opacity-90 transition transform active:scale-95"
          >
            {messages.nextButton}
          </button>
        )}
      </div>
    </div>
  );
}
